/**
 * <Mark> — the one small colored mark that sits ON the thing an alert is about
 * (The Quiet Toolbar, 10/10; mockup https://claude.ai/artifact/867xubbA5qmtEe3s5KsP2c
 * "Marks on the thing"). Four colors, each with ONE meaning:
 *
 *   blocked  red    Can't move until someone decides.
 *   waiting  amber  Our turn, and it's getting old.
 *   new      blue   A customer, a vendor or a coworker said something.
 *   back     green  Done by someone else. Your confirm closes it.
 *
 * The rule that makes it quiet: a mark is DERIVED FROM THE RECORD'S OWN STATE
 * at render time — never from a notification row. Doing the work changes the
 * record, and the mark goes away. Nobody "reads" it and nothing is stored.
 *
 * Meaning never rides on color alone (visual-first, 7th grade): every mark has
 * an icon and a word or two, and a screen reader hears "<kind>: <label>".
 */
import type { ReactNode } from "react";
import { Clock, CornerUpLeft, Flag, MessageCircle, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export type MarkKind = "blocked" | "waiting" | "new" | "back";

export const MARK_LOOK: Record<MarkKind, { word: string; icon: LucideIcon; className: string }> = {
    blocked: {
        word: "Blocked",
        icon: Flag,
        className: "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200",
    },
    waiting: {
        word: "Waiting on us",
        icon: Clock,
        className: "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200",
    },
    new: {
        word: "New from them",
        icon: MessageCircle,
        className: "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200",
    },
    back: {
        word: "Came back",
        icon: CornerUpLeft,
        className: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200",
    },
};

export function Mark({
    kind,
    label,
    title,
    className,
    icon,
    "data-testid": testId,
}: {
    kind: MarkKind;
    /** The words on the mark. Defaults to the kind's own word. */
    label?: ReactNode;
    /** Hover text: say what to do about it. */
    title?: string;
    className?: string;
    /** Override the kind's icon (e.g. a warning triangle on a bounce). */
    icon?: LucideIcon;
    "data-testid"?: string;
}) {
    const look = MARK_LOOK[kind];
    const Icon = icon ?? look.icon;
    const text = label ?? look.word;
    return (
        <span
            className={cn(
                "inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-bold",
                look.className,
                className,
            )}
            data-mark={kind}
            data-testid={testId ?? `mark-${kind}`}
            title={title}
        >
            <Icon className="h-3 w-3" aria-hidden />
            <span className="sr-only">{look.word}: </span>
            {text}
        </span>
    );
}
